import { Autocomplete, TextField } from "@mui/material";
import CheckboxQuestion from "components/shared/CheckboxQuestion";
import DateFieldPicker from "components/shared/DateFieldPicker";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import TextButton from "components/shared/TextButton";
import TitleSeparation from "components/shared/TitleSeparation";
import type {
    IBeneficiar,
    IBeneficiaryHeir,
    ISuccessionHeirPayload,
    ISuccessionPreview,
    ISuccessionPreviewTerm,
} from "containers/Beneficiars/types";
import type { ISocialProfileOption } from "containers/NewBeneficiaryContainer";
import { socialProfileList } from "containers/NewBeneficiaryContainer";
import { format } from "date-fns";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { createSuccession, fetchSuccessionPreview, updateSuccession } from "store/beneficiaryStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { BeneficiaryVAT } from "store/types";

import variables from "../../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";

type Option = { value: number; label: string };
type ExistingOption = { id: number; label: string };

interface IHeirRow {
    mode: "existing" | "new";
    existing: ExistingOption | null;
    surname: string;
    name: string;
    email: string;
    street: string;
    zipCode: string;
    city: string;
    country: string;
    sharePercent: number | "";
    socialProfileType: ISocialProfileOption | null;
    vat: Option;
    precount: Option;
    socialSecurityNumber: string;
    siretNumber: string;
}

interface IFormValues {
    heirs: IHeirRow[];
    deceasedAt: Date | null;
    transferUnamortizedAdvances: boolean;
}

interface ISuccessionModal {
    open: boolean;
    mode: "create" | "update";
    beneficiary: IBeneficiar;
    existingHeirs: IBeneficiaryHeir[];
    /** `saved` is true when the succession was written and the page should refresh. */
    onClose: (saved: boolean) => void;
}

const SHARE_DECIMALS = 4;
const TOTAL_SHARE = 100;

const round = (value: number, decimals: number) => {
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
};

const sumShares = (heirs: IHeirRow[]) =>
    round(
        heirs.reduce((sum, heir) => sum + (Number(heir.sharePercent) || 0), 0),
        SHARE_DECIMALS,
    );

/** Mirrors the backend: percentages are scaled by the share, thresholds are untouched. */
const scalePercentage = (percentage: number, sharePercent: number) =>
    round((percentage * sharePercent) / TOTAL_SHARE, SHARE_DECIMALS);

const milestonePercentages = (data: string): number[] => {
    try {
        const milestones = JSON.parse(data);
        return Array.isArray(milestones) ? milestones.map((m) => +m.percentage) : [];
    } catch {
        return [];
    }
};

const fullName = (person: { surname?: string | null; name?: string | null }) =>
    `${person.surname?.toUpperCase() ?? ""} ${person.name ?? ""}`.trim();

const heirLabel = (heir: IHeirRow) =>
    heir.mode === "existing" ? heir.existing?.label ?? "" : fullName({ surname: heir.surname, name: heir.name });

const SuccessionModal = ({ open, mode, beneficiary, existingHeirs, onClose }: ISuccessionModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companyInfos, countryFeatures, getCompanySettings } = useCompanySettings();
    const { successionStatus, beneficiarsAutocomplete } = useAppSelector((state) => state.beneficiary);

    const usesTwentyPercentVat = getCompanySettings("uses_twenty_percent_vat");
    const socialProfiles: ISocialProfileOption[] = socialProfileList[(companyInfos?.distributorType ?? 0) - 1] ?? [];
    const usesSocialProfileType = countryFeatures.precountMode === "social_profile_type";

    const vatList: Option[] = useMemo(
        () =>
            countryFeatures.vat.options
                .filter((o) => o.value !== BeneficiaryVAT.TWENTY_PERCENT || usesTwentyPercentVat)
                .map((o) => ({ value: o.value, label: t(o.label) })),
        [countryFeatures.vat.options, usesTwentyPercentVat, t],
    );
    const precountOptions: Option[] = useMemo(
        () => [
            { value: 0, label: t("Beneficiars.PrecountNo") },
            { value: 1, label: t("Beneficiars.PrecountYes") },
        ],
        [t],
    );

    const emptyRow = (): IHeirRow => ({
        mode: "existing",
        existing: null,
        surname: "",
        name: "",
        email: "",
        street: beneficiary.street ?? "",
        zipCode: beneficiary.zipCode ?? "",
        city: beneficiary.city ?? "",
        country: beneficiary.country ?? "",
        sharePercent: "",
        socialProfileType: socialProfiles[0] ?? null,
        vat: vatList[0],
        precount: precountOptions[0],
        socialSecurityNumber: "",
        siretNumber: "",
    });

    const defaultValues: IFormValues = useMemo(
        () => ({
            heirs:
                mode === "update" && existingHeirs.length
                    ? existingHeirs.map((heir) => ({
                          mode: "existing" as const,
                          existing: { id: heir.heirBeneficiarId, label: fullName(heir) },
                          surname: "",
                          name: "",
                          email: "",
                          street: "",
                          zipCode: "",
                          city: "",
                          country: "",
                          sharePercent: heir.sharePercent,
                          socialProfileType:
                              socialProfiles.find((o) => o.type === heir.socialProfileType) ??
                              socialProfiles[0] ??
                              null,
                          vat: vatList.find((o) => o.value === heir.vat) ?? vatList[0],
                          precount: precountOptions.find((o) => o.value === heir.precount) ?? precountOptions[0],
                          socialSecurityNumber: heir.socialSecurityNumber ?? "",
                          siretNumber: heir.siretNumber ?? "",
                      }))
                    : [emptyRow()],
            deceasedAt: null,
            transferUnamortizedAdvances: true,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mode, existingHeirs],
    );

    const {
        control,
        formState: { errors },
        handleSubmit,
        watch,
        setValue,
    } = useForm<IFormValues>({ defaultValues, mode: "all" });
    const { fields, append, remove } = useFieldArray({ control, name: "heirs" });

    const heirs = watch("heirs");
    const total = sumShares(heirs);
    const isTotalComplete = total === TOTAL_SHARE;
    const areHeirsComplete = heirs.every((heir) =>
        heir.mode === "existing" ? !!heir.existing : heir.surname.trim().length > 0,
    );

    /* ---- steps -------------------------------------------------------------- */
    const steps = mode === "create" ? (["heirs", "terms", "recap"] as const) : (["heirs", "recap"] as const);
    const [stepIndex, setStepIndex] = useState(0);
    const step = steps[stepIndex];

    /* ---- existing beneficiary search --------------------------------------- */
    const { handleQuery, handleRequest, requestStatus } = useAPIPagination({
        type: "beneficiaries",
        defaultLimitPagination: 10,
        autocomplete: true,
    });
    // The shared autocomplete list may predate a succession recorded on this same screen: reload it on open.
    useEffect(() => {
        if (open) handleRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const chosenIds = heirs.map((heir) => heir.existing?.id).filter((id): id is number => !!id);
    const existingOptions: ExistingOption[] = useMemo(
        () =>
            beneficiarsAutocomplete
                .filter((b) => b.id !== beneficiary.id && !b.deceasedAt)
                .map((b) => ({ id: b.id, label: fullName(b) })),
        [beneficiarsAutocomplete, beneficiary.id],
    );

    /* ---- terms preview (create only) --------------------------------------- */
    const [preview, setPreview] = useState<ISuccessionPreview | null>(null);

    useEffect(() => {
        if (!open || mode !== "create" || !beneficiary?.id) return;
        dispatch(fetchSuccessionPreview(beneficiary.id)).then((response) => {
            if (response.meta.requestStatus === "fulfilled") {
                setPreview(response.payload as ISuccessionPreview);
            }
        });
    }, [open, mode, beneficiary?.id, dispatch]);

    const hasBlockedTerms = !!preview?.terms.some((term) => term.blockedByPayment);
    const hasUnsplitPackageChannels = !!preview?.terms.some((term) =>
        term.channels.some((channel) => channel.isPackageChannel),
    );
    const activeTermCount = preview?.terms.length ?? 0;
    // Every active term is transmitted. One paid contract blocks the whole succession.
    const canTransmitTerms = activeTermCount > 0 && !hasBlockedTerms;
    const deceasedEndDate = preview?.terms[0]?.deceasedEndDate;

    /* ---- submit ------------------------------------------------------------ */
    const toPayloadHeir = (row: IHeirRow): ISuccessionHeirPayload => ({
        beneficiaryId: row.mode === "existing" ? row.existing?.id ?? null : null,
        newBeneficiary:
            row.mode === "new"
                ? {
                      surname: row.surname.trim(),
                      name: row.name.trim(),
                      email: row.email.trim() || null,
                      street: row.street.trim(),
                      zipCode: row.zipCode.trim(),
                      city: row.city.trim(),
                      country: row.country.trim(),
                  }
                : null,
        sharePercent: Number(row.sharePercent),
        socialProfile: {
            socialProfileType: usesSocialProfileType ? row.socialProfileType?.type ?? "" : "",
            vat: row.vat.value,
            precount: row.precount.value,
            socialSecurityNumber: row.socialSecurityNumber || undefined,
            siretNumber: row.siretNumber || undefined,
        },
    });

    const onSubmit = async (data: IFormValues) => {
        if (!isTotalComplete || !areHeirsComplete) return;
        if (mode === "create" && !canTransmitTerms) return;

        const payloadHeirs = data.heirs.map(toPayloadHeir);
        const response =
            mode === "create"
                ? await dispatch(
                      createSuccession({
                          beneficiaryId: beneficiary.id,
                          data: {
                              deceasedAt: data.deceasedAt ? format(data.deceasedAt, "yyyy-MM-dd") : null,
                              transferUnamortizedAdvances: data.transferUnamortizedAdvances,
                              heirs: payloadHeirs,
                          },
                      }),
                  )
                : await dispatch(updateSuccession({ beneficiaryId: beneficiary.id, heirs: payloadHeirs }));

        if (response.meta.requestStatus === "fulfilled") onClose(true);
    };

    const canGoNext =
        step === "heirs" ? isTotalComplete && areHeirsComplete : step === "terms" ? canTransmitTerms : true;
    const canConfirm = isTotalComplete && areHeirsComplete && (mode === "update" || canTransmitTerms);

    /* ---- render helpers ---------------------------------------------------- */
    const renderHeirRow = (field: (typeof fields)[number], index: number) => {
        const row = heirs[index];
        const chosenElsewhere = chosenIds.filter((id) => id !== row?.existing?.id);

        return (
            <div key={field.id} className={styles["heir-row"]}>
                <div className={styles["heir-row-header"]}>
                    <span className={styles["heir-row-title"]}>{t("Heirs.Modal.Heir", { index: index + 1 })}</span>
                    <div className={styles["heir-row-actions"]}>
                        {mode === "create" && (
                            <TextButton
                                text={
                                    row?.mode === "existing" ? t("Heirs.Modal.CreateNew") : t("Heirs.Modal.UseExisting")
                                }
                                handleClick={() =>
                                    setValue(`heirs.${index}.mode`, row?.mode === "existing" ? "new" : "existing")
                                }
                                underlineOnHover
                                style={{ fontSize: "0.8rem", color: variables["info"] }}
                            />
                        )}
                        {fields.length > 1 && (
                            <TextButton
                                text={t("Heirs.Modal.RemoveHeir")}
                                handleClick={() => remove(index)}
                                underlineOnHover
                                style={{ fontSize: "0.8rem", color: variables["danger-text"] }}
                            />
                        )}
                    </div>
                </div>

                <div className={styles["double-field"]}>
                    {row?.mode === "existing" ? (
                        <div className="form-component" style={{ flex: 1 }}>
                            <label className="form-component-label">{t("Heirs.Modal.SearchBeneficiary")}*</label>
                            <Autocomplete
                                options={existingOptions.filter((o) => !chosenElsewhere.includes(o.id))}
                                value={row.existing}
                                loading={requestStatus === "pending"}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText={t("Heirs.Modal.NoBeneficiaryFound")}
                                onInputChange={(_, value, reason) => {
                                    if (reason !== "input") return;
                                    handleQuery({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
                                }}
                                onChange={(_, value) => setValue(`heirs.${index}.existing`, value)}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={t("Heirs.Modal.SearchBeneficiary")} required />
                                )}
                            />
                        </div>
                    ) : (
                        <>
                            <TextFormComponent
                                control={control}
                                controlName={`heirs.${index}.surname`}
                                label={t("Representatives.Form.Surname")}
                                required
                                error={errors.heirs?.[index]?.surname}
                            />
                            <TextFormComponent
                                control={control}
                                controlName={`heirs.${index}.name`}
                                label={t("Representatives.Form.Name")}
                                error={errors.heirs?.[index]?.name}
                            />
                        </>
                    )}
                    <NumberFormComponent
                        control={control}
                        controlName={`heirs.${index}.sharePercent`}
                        label={t("Heirs.Modal.SharePercent")}
                        required
                        float
                        error={errors.heirs?.[index]?.sharePercent}
                        sx={{ maxWidth: "9rem" }}
                    />
                </div>
                {row?.mode === "new" && (
                    <>
                        <TextFormComponent
                            control={control}
                            controlName={`heirs.${index}.email`}
                            label={t("Beneficiars.Email")}
                            error={errors.heirs?.[index]?.email}
                        />
                        <TextFormComponent
                            control={control}
                            controlName={`heirs.${index}.street`}
                            label={t("NewBeneficiary.StreetNumber")}
                            error={errors.heirs?.[index]?.street}
                        />
                        <div className={`${styles["double-field"]} ${styles["thirty"]}`}>
                            <TextFormComponent
                                control={control}
                                controlName={`heirs.${index}.zipCode`}
                                label={t("NewBeneficiary.ZIPCode")}
                                error={errors.heirs?.[index]?.zipCode}
                            />
                            <TextFormComponent
                                control={control}
                                controlName={`heirs.${index}.city`}
                                label={t("NewBeneficiary.City")}
                                error={errors.heirs?.[index]?.city}
                            />
                        </div>
                        <TextFormComponent
                            control={control}
                            controlName={`heirs.${index}.country`}
                            label={t("NewBeneficiary.Country")}
                            error={errors.heirs?.[index]?.country}
                        />
                    </>
                )}

                <TitleSeparation
                    title={t("Heirs.Modal.ProfileTitle")}
                    description={t("Heirs.Modal.ProfileInfo")}
                    titleStyle={{ fontSize: "13px" }}
                />
                <div className={styles["double-field"]}>
                    {usesSocialProfileType ? (
                        <AutocompleteFormComponent
                            label={`${t("NewBeneficiary.SocialProfile")}`}
                            options={socialProfiles}
                            renderOptionText={(option: ISocialProfileOption) =>
                                `${option.type} | ${option.description}`
                            }
                            defaultFilterOptions
                            control={control}
                            controlName={`heirs.${index}.socialProfileType`}
                            required
                            error={errors.heirs?.[index]?.socialProfileType}
                            isOptionEqualToValue={(option, value) => option.type === value.type}
                        />
                    ) : countryFeatures.precountMode === "direct" && countryFeatures.precount.options.length > 0 ? (
                        <AutocompleteFormComponent
                            label={t("Representatives.Form.PrecountLabel")}
                            control={control}
                            controlName={`heirs.${index}.precount`}
                            options={precountOptions}
                            required
                            error={errors.heirs?.[index]?.precount}
                            renderOptionText={(option) => `${option.label}`}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                        />
                    ) : null}
                    <AutocompleteFormComponent
                        control={control}
                        controlName={`heirs.${index}.vat`}
                        options={vatList}
                        required
                        error={errors.heirs?.[index]?.vat}
                        label={t("Representatives.Form.FiscalProfile")}
                        renderOptionText={(option) => `${option.label ?? ""}`}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                    />
                </div>
                <div className={styles["double-field"]}>
                    <TextFormComponent
                        control={control}
                        controlName={`heirs.${index}.socialSecurityNumber`}
                        label={t("Beneficiars.SecurityNumber")}
                        error={errors.heirs?.[index]?.socialSecurityNumber}
                    />
                    <TextFormComponent
                        control={control}
                        controlName={`heirs.${index}.siretNumber`}
                        label={t("Beneficiars.Siret")}
                        error={errors.heirs?.[index]?.siretNumber}
                    />
                </div>
            </div>
        );
    };

    const renderTerm = (term: ISuccessionPreviewTerm) => {
        /** Cents. Negative = advance still to recoup, which only follows the heirs when
         *  « transmettre les à-valoir non amortis » stays checked at the last step. */
        const balance = term.openingBalance ?? 0;

        return (
            <div
                key={term.id}
                className={`${styles["term-row"]} ${term.blockedByPayment ? styles["term-row--blocked"] : ""}`}
            >
                <div className={styles["term-body"]}>
                    <div className={styles["term-title"]}>
                        <strong className={styles["term-name"]}>{term.title ?? `#${term.titleId}`}</strong>
                        {term.reference && <span className={styles["term-reference"]}>{term.reference}</span>}
                        {balance !== 0 && (
                            <span
                                className={`${styles["term-balance"]} ${
                                    balance < 0 ? styles["term-balance--debt"] : styles["term-balance--credit"]
                                }`}
                            >
                                {balance < 0
                                    ? t("Heirs.Modal.TermBalanceToRecoup", {
                                          amount: formatNumber(Math.abs(balance) / 100, {
                                              isCurrency: true,
                                              always2Digit: true,
                                          }),
                                      })
                                    : t("Heirs.Modal.TermBalanceCarried", {
                                          amount: formatNumber(balance / 100, { isCurrency: true, always2Digit: true }),
                                      })}
                            </span>
                        )}
                    </div>
                    {term.blockedByPayment && (
                        <span className={styles["term-blocked"]}>{t("Heirs.Modal.TermBlockedByPayment")}</span>
                    )}
                    {term.endDate && (
                        // Two contracts on the same title are otherwise indistinguishable.
                        <span className={styles["term-meta"]}>
                            {t("Heirs.Modal.TermEndDate", {
                                date: formatLocaleDateString({ date: term.endDate }),
                            })}
                        </span>
                    )}
                    {term.channels.map((channel) => {
                        const percentages = milestonePercentages(channel.data);
                        // A forfait is handed over whole: its milestones are copied verbatim onto every
                        // heir, so prorating them here would announce an amount nobody will receive.
                        const heirPercentages = (share: number) =>
                            channel.isPackageChannel ? percentages : percentages.map((p) => scalePercentage(p, share));

                        return (
                            <div key={channel.channelId} className={styles["term-channel"]}>
                                <span>
                                    {t("Heirs.Modal.Milestones")} : {percentages.map((p) => `${p} %`).join(" / ")}
                                    {channel.isPackageChannel && ` (${t("Heirs.Modal.PackageChannel")})`}
                                </span>
                                <ul>
                                    {heirs.map((heir, i) => (
                                        <li key={i}>
                                            {heirLabel(heir) || t("Heirs.Modal.Heir", { index: i + 1 })} (
                                            {Number(heir.sharePercent) || 0} %) →{" "}
                                            {heirPercentages(Number(heir.sharePercent) || 0)
                                                .map((p) => `${p} %`)
                                                .join(" / ")}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const deceasedName = fullName(beneficiary);

    return (
        <ModalBase
            open={open}
            onClose={() => onClose(false)}
            modalTitle={mode === "create" ? t("Heirs.Modal.TitleCreate") : t("Heirs.Modal.TitleUpdate")}
            modalSubtitle={
                mode === "create"
                    ? t("Heirs.Modal.SubtitleCreate", { name: deceasedName })
                    : t("Heirs.Modal.SubtitleUpdate")
            }
            widthTransition={{ width: "760px", active: true }}
        >
            <div className={styles["succession-modal"]}>
                <div className={styles["steps"]}>
                    {steps.map((s, i) => {
                        const state = i < stepIndex ? "done" : i === stepIndex ? "active" : "todo";
                        return (
                            <Fragment key={s}>
                                <div className={`${styles["step"]} ${styles[`step--${state}`]}`}>
                                    <span className={styles["step-badge"]}>{i < stepIndex ? "✓" : i + 1}</span>
                                    <span className={styles["step-label"]}>
                                        {s === "heirs"
                                            ? t("Heirs.Modal.StepHeirs")
                                            : s === "terms"
                                              ? t("Heirs.Modal.StepTerms")
                                              : t("Heirs.Modal.StepRecap")}
                                    </span>
                                </div>
                                {i < steps.length - 1 && <span className={styles["step-connector"]} />}
                            </Fragment>
                        );
                    })}
                </div>

                <form className={styles["form"]} onSubmit={handleSubmit(onSubmit)}>
                    {step === "heirs" && (
                        <>
                            {fields.map(renderHeirRow)}
                            <div className={styles["heirs-footer"]}>
                                <TextButton
                                    text={`+ ${t("Heirs.Modal.AddHeir")}`}
                                    handleClick={() => append(emptyRow())}
                                    underlineOnHover
                                    style={{ fontSize: "0.85rem", color: variables["foreground"] }}
                                />
                                <span
                                    className={`${styles["total"]} ${
                                        isTotalComplete ? styles["total--ok"] : styles["total--ko"]
                                    }`}
                                >
                                    {t("Heirs.Modal.Total")} : {total} %
                                </span>
                            </div>
                            {!isTotalComplete && <p className={styles["hint"]}>{t("Heirs.Modal.TotalMustBe100")}</p>}
                            {!areHeirsComplete && <p className={styles["hint"]}>{t("Heirs.Modal.MissingHeir")}</p>}
                        </>
                    )}

                    {step === "terms" && (
                        <>
                            <TitleSeparation
                                title={t("Heirs.Modal.TermsTitle")}
                                description={t("Heirs.Modal.TermsAll", { count: activeTermCount })}
                                titleStyle={{ fontSize: "14px" }}
                            />
                            {hasBlockedTerms && (
                                <p className={styles["warning"]}>{t("Heirs.Modal.BlockedByPayment")}</p>
                            )}
                            {hasUnsplitPackageChannels && (
                                <p className={styles["warning"]}>{t("Heirs.Modal.PackageChannelsNotSplit")}</p>
                            )}
                            {preview && preview.terms.length === 0 && (
                                <p className={styles["hint"]}>{t("Heirs.Modal.NoTerms")}</p>
                            )}
                            {preview?.terms.map(renderTerm)}
                        </>
                    )}

                    {step === "recap" && (
                        <>
                            <p className={styles["recap-line"]}>
                                {t("Heirs.Modal.Summary", {
                                    heirs: heirs.length,
                                    terms:
                                        mode === "create"
                                            ? activeTermCount
                                            : existingHeirs[0]?.inheritedTermsCount ?? 0,
                                })}
                            </p>
                            {mode === "create" && (
                                <>
                                    {deceasedEndDate && (
                                        <p className={styles["recap-line"]}>
                                            {t("Heirs.Modal.SwitchPeriod", {
                                                name: deceasedName,
                                                date: formatLocaleDateString({ date: deceasedEndDate }),
                                            })}
                                        </p>
                                    )}
                                    <CheckboxQuestion
                                        checked={watch("transferUnamortizedAdvances")}
                                        onChange={() =>
                                            setValue(
                                                "transferUnamortizedAdvances",
                                                !watch("transferUnamortizedAdvances"),
                                            )
                                        }
                                        text={t("Heirs.Modal.TransferAdvances")}
                                        textStyle={{ color: variables.foreground } as React.CSSProperties}
                                        infoTooltip={{ text: t("Heirs.Modal.TransferAdvancesTooltip"), type: "info" }}
                                    />
                                    <div className={styles["date-field"]}>
                                        <span>{t("Heirs.Modal.DeceasedAt")}</span>
                                        <DateFieldPicker
                                            value={watch("deceasedAt")}
                                            onChange={(date: Date | null) => setValue("deceasedAt", date)}
                                        />
                                    </div>
                                </>
                            )}
                            <p className={styles["warning"]}>{t("Heirs.Modal.Irreversible")}</p>
                        </>
                    )}
                </form>

                <div className={styles["action-btns"]}>
                    {stepIndex > 0 && (
                        <StyledButton buttonType="secondary" type="button" onClick={() => setStepIndex(stepIndex - 1)}>
                            {t("Heirs.Modal.Back")}
                        </StyledButton>
                    )}
                    {stepIndex < steps.length - 1 ? (
                        <StyledButton
                            buttonType="primary"
                            type="button"
                            disabled={!canGoNext}
                            onClick={() => setStepIndex(stepIndex + 1)}
                        >
                            {t("Heirs.Modal.Next")}
                        </StyledButton>
                    ) : (
                        <StyledButton
                            buttonType="primary"
                            type="button"
                            disabled={!canConfirm}
                            loading={successionStatus === "pending"}
                            onClick={() => handleSubmit(onSubmit)()}
                        >
                            {mode === "create" ? t("Heirs.Modal.Confirm") : t("Heirs.Modal.ConfirmUpdate")}
                        </StyledButton>
                    )}
                </div>
            </div>
        </ModalBase>
    );
};

export default SuccessionModal;
